import React, { useState, useEffect, useRef } from 'react';
import {
  Database, MessageSquare, Plus, Trash2, RefreshCw, Play, Send, ChevronLeft, ChevronRight,
  Table, Code, CheckCircle, AlertTriangle, Copy, Lock, KeyRound, Download, Radio, Square,
  Shield, Terminal, Edit, Users, FileDown, Eye, ChevronUp, ChevronDown, RotateCcw, Heart, Crown,
  Server, XCircle, Pencil, Activity, Cpu, HardDrive, MemoryStick, Search
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { cn } from '../lib/utils';
import apiService from '../services/api';
import websocketService from '../services/websocket';
import SSHTerminal from './SSHTerminal';
import KsqlDBManager from './KsqlDBManager';
import ACLManager from './ACLManager';

// JSON Viewer Component
const JsonViewer = ({ data, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  if (!data) return <span className="text-muted-foreground italic">null</span>;
  
  let isJson = false;
  let formatted = data;
  
  try {
    const parsed = JSON.parse(data);
    formatted = JSON.stringify(parsed, null, 2);
    isJson = true;
  } catch {
    formatted = data;
  }
  
  if (!isJson) {
    return <span className="font-mono text-xs break-all">{data}</span>;
  }
  
  return (
    <div className="relative">
      {expanded ? (
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            className="absolute top-0 right-0 h-6 px-2 text-xs z-10"
            onClick={() => setExpanded(false)}
          >
            <ChevronUp className="h-3 w-3 mr-1" />Collapse
          </Button>
          <pre className="text-xs bg-gray-900 text-green-400 p-3 pr-20 rounded-lg overflow-auto max-h-[400px] font-mono whitespace-pre-wrap">
            {formatted}
          </pre>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span 
            className="font-mono text-xs text-muted-foreground truncate max-w-[300px] cursor-pointer hover:text-foreground"
            onClick={() => setExpanded(true)}
          >
            {data.length > 60 ? data.substring(0, 60) + '...' : data}
          </span>
          {data.length > 60 && (
            <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => setExpanded(true)}>
              <Eye className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Health Status Card Component
const HealthStatusCard = ({ health, loading, onRefresh, nodes, onSSH, nodeStats, nodeStatsLoading, onRefreshStats }) => {
  // All hooks must be called unconditionally at the top
  const [showBrokerDetails, setShowBrokerDetails] = useState(false);
  const [selectedView, setSelectedView] = useState('brokers');
  
  // Calculate aggregate stats - must be called unconditionally
  const aggregateStats = React.useMemo(() => {
    if (!nodeStats || Object.keys(nodeStats).length === 0) return null;
    const stats = Object.values(nodeStats);
    const avgCpu = Math.round(stats.reduce((sum, s) => sum + (s.cpu || 0), 0) / stats.length);
    const avgMem = Math.round(stats.reduce((sum, s) => sum + (s.memPercent || 0), 0) / stats.length);
    const avgDisk = Math.round(stats.reduce((sum, s) => sum + (s.diskPercent || 0), 0) / stats.length);
    const maxCpu = Math.max(...stats.map(s => s.cpu || 0));
    const maxMem = Math.max(...stats.map(s => s.memPercent || 0));
    const maxDisk = Math.max(...stats.map(s => s.diskPercent || 0));
    return { avgCpu, avgMem, avgDisk, maxCpu, maxMem, maxDisk };
  }, [nodeStats]);

  // Early returns after all hooks
  if (loading && !health) {
    return (
      <div className="p-3 rounded-lg border border-border/50 bg-card">
        <div className="flex items-center justify-center py-3">
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-xs text-muted-foreground">Loading cluster health...</span>
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <div className="p-3 rounded-lg border border-border/50 bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Heart className="h-4 w-4" />
            <span className="text-xs">Health status not loaded</span>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh} className="h-6 text-xs px-2">
            <RefreshCw className="h-3 w-3 mr-1" />Check
          </Button>
        </div>
      </div>
    );
  }

  // Helper functions (not hooks)
  const getStatusBadge = (status) => {
    switch (status) {
      case 'healthy': return <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-[10px] px-1.5 py-0"><CheckCircle className="h-3 w-3 mr-1" />HEALTHY</Badge>;
      case 'degraded': return <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30 text-[10px] px-1.5 py-0"><AlertTriangle className="h-3 w-3 mr-1" />DEGRADED</Badge>;
      case 'critical': return <Badge className="bg-red-500/20 text-red-500 border-red-500/30 text-[10px] px-1.5 py-0"><XCircle className="h-3 w-3 mr-1" />CRITICAL</Badge>;
      default: return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30 text-[10px] px-1.5 py-0"><AlertTriangle className="h-3 w-3 mr-1" />UNKNOWN</Badge>;
    }
  };

  const controllerId = health.controller?.id;

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${units[i]}`;
  };

  // Resource bar component
  const ResourceBar = ({ value, label, icon: Icon, color }) => (
    <div className="flex items-center gap-2">
      <Icon className={cn("h-3.5 w-3.5", color)} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between text-[10px] mb-0.5">
          <span className="text-muted-foreground">{label}</span>
          <span className={cn(
            "font-medium",
            value > 80 ? "text-red-500" : value > 60 ? "text-amber-500" : "text-green-500"
          )}>{value}%</span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all",
              value > 80 ? "bg-red-500" : value > 60 ? "bg-amber-500" : "bg-green-500"
            )}
            style={{ width: `${Math.min(value, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-border/50 bg-card overflow-hidden">
      {/* Header Row: Status + Key Metrics + Actions */}
      <div className="p-3 flex items-center justify-between gap-3 border-b border-border/30">
        <div className="flex items-center gap-4 flex-1 min-w-0 flex-wrap">
          {/* Status */}
          <div className="flex items-center gap-2">
            <Heart className={cn(
              "h-4 w-4",
              health.status === 'healthy' ? "text-green-500" :
              health.status === 'degraded' ? "text-amber-500" : "text-red-500"
            )} />
            {getStatusBadge(health.status)}
          </div>

          <div className="h-5 w-px bg-border/50 hidden sm:block" />

          {/* Quick Stats with Labels */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5" title={`${health.brokers?.alive} of ${health.brokers?.total} brokers online`}>
              <Server className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-[10px] text-muted-foreground">Brokers:</span>
              <span className={cn("text-xs font-medium", health.brokers?.hasMajority ? "text-green-500" : "text-red-500")}>
                {health.brokers?.alive}/{health.brokers?.total}
              </span>
            </div>
            <div className="flex items-center gap-1.5" title={`${health.topics?.total || 0} topics in cluster`}>
              <Database className="h-3.5 w-3.5 text-cyan-500" />
              <span className="text-[10px] text-muted-foreground">Topics:</span>
              <span className="text-xs font-medium">{health.topics?.total || 0}</span>
            </div>
            <div className="flex items-center gap-1.5" title={`${health.partitions?.total || 0} partitions total`}>
              <Activity className="h-3.5 w-3.5 text-purple-500" />
              <span className="text-[10px] text-muted-foreground">Partitions:</span>
              <span className="text-xs font-medium">{health.partitions?.total || 0}</span>
              {(health.partitions?.underReplicated > 0 || health.partitions?.offline > 0) && (
                <span className="text-[10px] text-amber-500">({health.partitions?.underReplicated}↓ {health.partitions?.offline}✗)</span>
              )}
            </div>
            <div className="flex items-center gap-1.5" title={`${(health.messages?.total || 0).toLocaleString()} messages (${formatBytes(health.messages?.sizeBytes)})`}>
              <MessageSquare className="h-3.5 w-3.5 text-green-500" />
              <span className="text-[10px] text-muted-foreground">Messages:</span>
              <span className="text-xs font-medium">{(health.messages?.total || 0).toLocaleString()}</span>
              {health.messages?.sizeBytes > 0 && (
                <span className="text-[10px] text-muted-foreground">({formatBytes(health.messages.sizeBytes)})</span>
              )}
            </div>
            <div className="flex items-center gap-1.5" title={`${health.consumerGroups?.total || 0} consumer groups`}>
              <Users className="h-3.5 w-3.5 text-orange-500" />
              <span className="text-[10px] text-muted-foreground">Groups:</span>
              <span className="text-xs font-medium">{health.consumerGroups?.total || 0}</span>
            </div>
          </div>

          {/* Aggregate Resource Summary */}
          {aggregateStats && (
            <>
              <div className="h-5 w-px bg-border/50 hidden md:block" />
              <div className="flex items-center gap-3 text-[10px] hidden md:flex">
                <div className="flex items-center gap-1" title={`CPU: avg ${aggregateStats.avgCpu}%, max ${aggregateStats.maxCpu}%`}>
                  <Cpu className="h-3 w-3 text-blue-400" />
                  <span className="text-muted-foreground">CPU:</span>
                  <span className={cn("font-medium", aggregateStats.maxCpu > 80 ? "text-red-500" : "text-foreground")}>
                    {aggregateStats.avgCpu}%
                  </span>
                </div>
                <div className="flex items-center gap-1" title={`Memory: avg ${aggregateStats.avgMem}%, max ${aggregateStats.maxMem}%`}>
                  <MemoryStick className="h-3 w-3 text-green-400" />
                  <span className="text-muted-foreground">Mem:</span>
                  <span className={cn("font-medium", aggregateStats.maxMem > 80 ? "text-red-500" : "text-foreground")}>
                    {aggregateStats.avgMem}%
                  </span>
                </div>
                <div className="flex items-center gap-1" title={`Disk: avg ${aggregateStats.avgDisk}%, max ${aggregateStats.maxDisk}%`}>
                  <HardDrive className="h-3 w-3 text-purple-400" />
                  <span className="text-muted-foreground">Disk:</span>
                  <span className={cn("font-medium", aggregateStats.maxDisk > 80 ? "text-red-500" : "text-foreground")}>
                    {aggregateStats.avgDisk}%
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] text-muted-foreground hidden lg:inline mr-1">
            {health.checkedAt ? new Date(health.checkedAt).toLocaleTimeString() : ''}
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRefresh} 
            disabled={loading}
            className="h-6 w-6 p-0"
            title="Refresh health"
          >
            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowBrokerDetails(!showBrokerDetails)}
            className="h-6 px-2"
            title={showBrokerDetails ? "Hide details" : "Show broker details"}
          >
            {showBrokerDetails ? (
              <><ChevronUp className="h-3 w-3 mr-1" /><span className="text-[10px]">Hide</span></>
            ) : (
              <><ChevronDown className="h-3 w-3 mr-1" /><span className="text-[10px]">Details</span></>
            )}
          </Button>
        </div>
      </div>

      {/* Issues Alert */}
      {health.issues?.length > 0 && (
        <div className="px-3 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2 text-xs text-red-500">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{health.issues.join(' • ')}</span>
        </div>
      )}

      {/* Expandable Details Section */}
      {showBrokerDetails && (
        <div className="border-t border-border/30">
          {/* Tab Navigation */}
          <div className="flex items-center gap-1 px-3 py-2 bg-muted/30 border-b border-border/30">
            <Button
              variant={selectedView === 'brokers' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => setSelectedView('brokers')}
            >
              <Server className="h-3 w-3 mr-1" />Brokers ({nodes?.length || 0})
            </Button>
            <Button
              variant={selectedView === 'resources' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => setSelectedView('resources')}
            >
              <Cpu className="h-3 w-3 mr-1" />Resources
            </Button>
            {selectedView === 'resources' && onRefreshStats && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 ml-auto"
                onClick={onRefreshStats}
                disabled={nodeStatsLoading}
                title="Refresh resource stats"
              >
                <RefreshCw className={cn("h-3 w-3", nodeStatsLoading && "animate-spin")} />
              </Button>
            )}
          </div>

          {/* Brokers View */}
          {selectedView === 'brokers' && (
            <div className="p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {nodes?.map((n, i) => {
                  const brokerId = n.brokerId !== undefined ? n.brokerId : i;
                  const isController = controllerId === brokerId;
                  const stats = nodeStats?.[n.host];
                  
                  return (
                    <div 
                      key={n.host}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg border transition-colors",
                        isController 
                          ? "bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20" 
                          : "bg-muted/30 border-border/50 hover:bg-muted/50"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {isController && <Crown className="h-3 w-3 text-amber-500" />}
                          <span className={cn("text-xs font-medium", isController ? "text-amber-500" : "text-foreground")}>
                            Broker {brokerId}
                          </span>
                          {isController && <Badge variant="outline" className="text-[8px] px-1 py-0 h-3">Controller</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-muted-foreground font-mono truncate">{n.host}</span>
                          {n.privateIp && n.privateIp !== n.host && (
                            <span className="text-[9px] text-muted-foreground/70">({n.privateIp})</span>
                          )}
                        </div>
                        {/* Compact Stats Row */}
                        {stats && (
                          <div className="flex items-center gap-2 mt-1 pt-1 border-t border-border/30">
                            <div className="flex items-center gap-0.5" title={`CPU: ${stats.cpu}%`}>
                              <Cpu className="h-2.5 w-2.5 text-blue-400" />
                              <span className={cn("text-[9px]", stats.cpu > 80 ? "text-red-500" : "text-muted-foreground")}>{stats.cpu}%</span>
                            </div>
                            <div className="flex items-center gap-0.5" title={`RAM: ${stats.memUsed}/${stats.memTotal}`}>
                              <MemoryStick className="h-2.5 w-2.5 text-green-400" />
                              <span className={cn("text-[9px]", stats.memPercent > 80 ? "text-red-500" : "text-muted-foreground")}>{stats.memPercent}%</span>
                            </div>
                            <div className="flex items-center gap-0.5" title={`Disk: ${stats.diskUsed}/${stats.diskTotal}`}>
                              <HardDrive className="h-2.5 w-2.5 text-purple-400" />
                              <span className={cn("text-[9px]", stats.diskPercent > 80 ? "text-red-500" : "text-muted-foreground")}>{stats.diskPercent}%</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 hover:bg-primary/20 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSSH && onSSH(n);
                        }}
                        title="Open SSH Terminal"
                      >
                        <Terminal className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Resources View */}
          {selectedView === 'resources' && (
            <div className="p-3">
              {!nodeStats || Object.keys(nodeStats).length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-xs">
                  {nodeStatsLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      <span>Loading resource stats...</span>
                    </div>
                  ) : (
                    <div>
                      <Cpu className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p>No resource data available</p>
                      <Button variant="outline" size="sm" onClick={onRefreshStats} className="mt-2 h-7 text-xs">
                        <RefreshCw className="h-3 w-3 mr-1" />Load Stats
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Aggregate Overview */}
                  {aggregateStats && (
                    <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
                      <div className="text-xs font-medium mb-3 flex items-center gap-2">
                        <Activity className="h-3.5 w-3.5" />
                        Cluster Average
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <ResourceBar value={aggregateStats.avgCpu} label="CPU" icon={Cpu} color="text-blue-400" />
                        <ResourceBar value={aggregateStats.avgMem} label="Memory" icon={MemoryStick} color="text-green-400" />
                        <ResourceBar value={aggregateStats.avgDisk} label="Disk" icon={HardDrive} color="text-purple-400" />
                      </div>
                    </div>
                  )}

                  {/* Per-Node Resources */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {nodes?.map((n, i) => {
                      const brokerId = n.brokerId !== undefined ? n.brokerId : i;
                      const isController = controllerId === brokerId;
                      const stats = nodeStats?.[n.host];
                      
                      if (!stats) return null;
                      
                      return (
                        <div 
                          key={n.host}
                          className={cn(
                            "p-3 rounded-lg border",
                            isController 
                              ? "bg-amber-500/5 border-amber-500/30" 
                              : "bg-card border-border/50"
                          )}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-1.5">
                              {isController && <Crown className="h-3 w-3 text-amber-500" />}
                              <span className="text-xs font-medium">Broker {brokerId}</span>
                            </div>
                            <span className="text-[10px] text-muted-foreground font-mono">{n.host}</span>
                          </div>
                          
                          <div className="space-y-2">
                            <ResourceBar value={stats.cpu} label="CPU" icon={Cpu} color="text-blue-400" />
                            <ResourceBar value={stats.memPercent} label={`Memory (${stats.memUsed}/${stats.memTotal})`} icon={MemoryStick} color="text-green-400" />
                            <ResourceBar value={stats.diskPercent} label={`Disk (${stats.diskUsed}/${stats.diskTotal})`} icon={HardDrive} color="text-purple-400" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClusterDashboard = ({ cluster, credentials: initialCredentials, onBack }) => {
  const [activeTab, setActiveTab] = useState('topics');
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [topicDetails, setTopicDetails] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);

  // Consumer Groups
  const [consumerGroups, setConsumerGroups] = useState([]);
  const [topicConsumerGroups, setTopicConsumerGroups] = useState([]);

  // Credentials & Dialogs
  const [credentials, setCredentials] = useState(initialCredentials || { username: '', password: '', privateKey: '', authType: 'password' });
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showCreateTopic, setShowCreateTopic] = useState(false);
  const [showEditTopic, setShowEditTopic] = useState(false);
  const [showJsonViewer, setShowJsonViewer] = useState(false);
  const [jsonViewerData, setJsonViewerData] = useState(null);
  const [showUninstallDialog, setShowUninstallDialog] = useState(false);
  const [showSSLDialog, setShowSSLDialog] = useState(false);
  const [uninstallConfirmText, setUninstallConfirmText] = useState('');

  // Forms
  const [newTopic, setNewTopic] = useState({ name: '', partitions: 3, replicationFactor: 1 });
  const [editConfig, setEditConfig] = useState({});
  const [newPartitions, setNewPartitions] = useState(3);
  const [produceKey, setProduceKey] = useState('');
  const [produceValue, setProduceValue] = useState('');

  // SQL & Stream
  const [sqlQuery, setSqlQuery] = useState('SELECT * FROM topic_name LIMIT 25');
  const [sqlResults, setSqlResults] = useState(null);
  const [sqlPageSize, setSqlPageSize] = useState(25);
  const [streamEnabled, setStreamEnabled] = useState(false);
  const [streamMessages, setStreamMessages] = useState([]);
  const [streamPageSize, setStreamPageSize] = useState(25);
  const streamCleanupRef = useRef(null);

  // Filter
  const [messageFilter, setMessageFilter] = useState('');
  const [topicSearch, setTopicSearch] = useState('');

  // SSH Terminal
  const [showSSHTerminal, setShowSSHTerminal] = useState(false);
  const [sshNode, setSSHNode] = useState(null);

  // Cluster Health
  const [clusterHealth, setClusterHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Node Stats (CPU, RAM, DISK)
  const [nodeStats, setNodeStats] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [nodeStatsLoading, setNodeStatsLoading] = useState(false);

  // Rename Dialog
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newClusterName, setNewClusterName] = useState('');

  // Add Nodes Dialog
  const [showAddNodesDialog, setShowAddNodesDialog] = useState(false);
  const [newNodes, setNewNodes] = useState([{ host: '', privateIp: '' }]);
  const [addNodesLoading, setAddNodesLoading] = useState(false);
  const [addNodesLogs, setAddNodesLogs] = useState([]);
  const [addNodeType, setAddNodeType] = useState('broker'); // 'broker' or 'full' (broker+zk/controller)

  // Prevent duplicate API calls
  const initialLoadDone = useRef(false);

  // Effects
  useEffect(() => {
    const saved = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (saved) setCredentials(JSON.parse(saved));
  }, [cluster.id]);

  useEffect(() => { 
    // Prevent double-loading in React StrictMode
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    
    loadTopics(); 
    loadClusterHealth();
    loadNodeStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cluster.id]);

  useEffect(() => {
    if (streamEnabled && selectedTopic) {
      websocketService.connect();
      websocketService.subscribeTopic(cluster.id, selectedTopic);
      const handleMsg = (data) => {
        if (data.topicName === selectedTopic && data.clusterId === cluster.id) {
          setStreamMessages(prev => [data.message, ...prev].slice(0, 500));
        }
      };
      websocketService.onTopicMessage(handleMsg);
      streamCleanupRef.current = () => { 
        websocketService.unsubscribeTopic(cluster.id, selectedTopic); 
        websocketService.off('topic-message'); 
      };
      return () => { if (streamCleanupRef.current) streamCleanupRef.current(); };
    }
  }, [streamEnabled, selectedTopic, cluster.id]);

  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) streamCleanupRef.current();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API Functions
  const testNetworkConnectivity = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.testNetworkConnectivity(cluster.id);
      if (res.allReachable) {
        setSuccess(`All brokers reachable! (${res.results.map(r => r.host + ':' + r.latency + 'ms').join(', ')})`);
      } else {
        const unreachable = res.results.filter(r => !r.reachable);
        setError(`Network issue: ${unreachable.map(r => r.host + ' - ' + r.error).join(', ')}. ${res.hint || ''}`);
      }
    } catch (e) { setError('Network test failed: ' + e.message); }
    finally { setLoading(false); setTimeout(() => { setSuccess(null); }, 5000); }
  };

  const loadClusterHealth = async () => {
    try {
      setHealthLoading(true);
      setError(null);
      const res = await apiService.getClusterHealth(cluster.id);
      if (res.success) {
        setClusterHealth(res.health);
      }
    } catch (e) {
      setError('Failed to check cluster health: ' + e.message);
    } finally {
      setHealthLoading(false);
    }
  };

  const loadNodeStats = async () => {
    // Get saved credentials - stats require SSH access
    const saved = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (!saved) {
      // No credentials saved, skip loading stats
      return;
    }
    
    try {
      setNodeStatsLoading(true);
      const creds = JSON.parse(saved);
      const res = await apiService.getNodeStats(cluster.id, creds);
      if (res.success) {
        setNodeStats(res.stats || {});
      }
    } catch (e) {
      console.warn('Failed to load node stats:', e.message);
      // Don't show error to user, stats are optional
    } finally {
      setNodeStatsLoading(false);
    }
  };

  const handleRenameCluster = async () => {
    if (!newClusterName.trim()) return;
    try {
      setLoading(true);
      const res = await apiService.renameCluster(cluster.id, newClusterName.trim());
      if (res.success) {
        cluster.name = newClusterName.trim();
        setSuccess('Cluster renamed successfully');
        setShowRenameDialog(false);
      }
    } catch (e) {
      setError('Failed to rename cluster: ' + e.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleAddNodes = async () => {
    // Validate nodes
    const validNodes = newNodes.filter(n => n.host.trim());
    if (validNodes.length === 0) {
      setError('Please add at least one node with a valid host');
      return;
    }

    // Check credentials
    const saved = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (!saved) {
      setPendingAction({ type: 'addNodes' });
      setShowAddNodesDialog(false);
      setShowCredentialsDialog(true);
      return;
    }

    const creds = JSON.parse(saved);
    
    try {
      setAddNodesLoading(true);
      setAddNodesLogs([]);
      setError(null);

      const res = await apiService.addNodesToCluster(cluster.id, {
        newNodes: validNodes.map(n => ({
          host: n.host.trim(),
          privateIp: n.privateIp?.trim() || null
        })),
        credentials: creds,
        nodeType: addNodeType  // 'broker' or 'full'
      });

      if (res.success) {
        setAddNodesLogs(res.logs || []);
        
        // Update local cluster with new nodes
        if (res.cluster) {
          cluster.nodes = res.cluster.nodes;
        }
        
        setSuccess(`Successfully added ${validNodes.length} node(s) to cluster`);
        setNewNodes([{ host: '', privateIp: '' }]);
        
        // Refresh health status
        loadClusterHealth();
        loadNodeStats();
      } else {
        setError(res.message || 'Failed to add nodes');
        setAddNodesLogs(res.logs || []);
      }
    } catch (e) {
      setError('Failed to add nodes: ' + (e.response?.data?.message || e.message));
    } finally {
      setAddNodesLoading(false);
      setTimeout(() => setSuccess(null), 5000);
    }
  };

  const addNewNodeRow = () => {
    setNewNodes([...newNodes, { host: '', privateIp: '' }]);
  };

  const removeNodeRow = (index) => {
    if (newNodes.length > 1) {
      setNewNodes(newNodes.filter((_, i) => i !== index));
    }
  };

  const updateNodeRow = (index, field, value) => {
    const updated = [...newNodes];
    updated[index][field] = value;
    setNewNodes(updated);
  };

  const loadTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getTopics(cluster.id);
      if (res.success) setTopics(res.topics || []);
    } catch (e) { 
      const errMsg = e.response?.data?.message || e.message;
      if (cluster.security === 'SSL' && errMsg.includes('broker')) {
        setError(`SSL Connection Failed: ${errMsg}. Try: 1) Download SSL certs first, 2) Check port 9092 is open, 3) Verify Kafka is running.`);
      } else {
        setError('Failed to load topics: ' + errMsg);
      }
    }
    finally { setLoading(false); }
  };

  const loadConsumerGroups = async () => {
    try {
      setLoading(true);
      const res = await apiService.getConsumerGroups(cluster.id);
      if (res.success) setConsumerGroups(res.groups || []);
    } catch { setError('Failed to load consumer groups'); }
    finally { setLoading(false); }
  };

  const loadTopicConsumerGroups = async (topic) => {
    try {
      const res = await apiService.getTopicConsumerGroups(cluster.id, topic);
      if (res.success) setTopicConsumerGroups(res.groups || []);
    } catch {}
  };

  const handleSelectTopic = async (topic) => {
    setSelectedTopic(topic);
    setMessages([]);
    setStreamMessages([]);
    setTopicDetails(null);
    setStreamEnabled(false);
    setCurrentPage(1);
    setTopicConsumerGroups([]);
    
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.describeTopic(cluster.id, topic);
      if (res.success) {
        setTopicDetails(res.topic);
        setNewPartitions(res.topic.partitionCount);
        setEditConfig(res.topic.config || {});
        loadTopicConsumerGroups(topic);
      }
    } catch (e) { setError('Failed to describe topic: ' + e.message); }
    finally { setLoading(false); }
  };

  const loadMessages = async (page = 1, size = pageSize) => {
    if (!selectedTopic || messagesLoading) return;
    
    setMessagesLoading(true);
    
    try {
      setError(null);
      const res = await apiService.getMessages(cluster.id, selectedTopic, size, page, false);
      
      if (res.success) {
        setMessages(res.messages || []);
        setTotalPages(res.totalPages || 1);
        setTotalMessages(res.total || 0);
        setCurrentPage(page);
        if (!res.messages?.length) {
          setSuccess('No messages found in topic');
          setTimeout(() => setSuccess(null), 3000);
        }
      }
    } catch (e) { 
      setError('Failed to load messages: ' + e.message); 
    }
    finally { 
      setMessagesLoading(false); 
    }
  };

  const handleCreateTopic = async () => {
    if (!newTopic.name) return setError('Topic name required');
    try {
      setLoading(true);
      await apiService.createTopic(cluster.id, newTopic.name, newTopic.partitions, Math.min(newTopic.replicationFactor, cluster.nodes.length));
      setSuccess('Topic created');
      setShowCreateTopic(false);
      setNewTopic({ name: '', partitions: 3, replicationFactor: 1 });
      loadTopics();
    } catch (e) { setError('Failed to create topic: ' + e.message); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleDeleteTopic = async (topic) => {
    if (!window.confirm(`Delete "${topic}"?`)) return;
    try {
      setLoading(true);
      await apiService.deleteTopic(cluster.id, topic);
      setSuccess('Topic deleted');
      if (selectedTopic === topic) { setSelectedTopic(null); setTopicDetails(null); }
      loadTopics();
    } catch { setError('Failed to delete topic'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleUpdateConfig = async () => {
    try {
      setLoading(true);
      await apiService.updateTopicConfig(cluster.id, selectedTopic, editConfig);
      setSuccess('Config updated');
      setShowEditTopic(false);
      handleSelectTopic(selectedTopic);
    } catch { setError('Failed to update config'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleAddPartitions = async () => {
    if (newPartitions <= topicDetails?.partitionCount) return setError('Must be greater than current');
    try {
      setLoading(true);
      await apiService.addTopicPartitions(cluster.id, selectedTopic, newPartitions);
      setSuccess('Partitions added');
      handleSelectTopic(selectedTopic);
    } catch { setError('Failed to add partitions'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleProduceMessage = async () => {
    if (!selectedTopic || !produceValue) return;
    try {
      setLoading(true);
      await apiService.produceMessage(cluster.id, selectedTopic, produceKey, produceValue);
      setSuccess('Message sent');
      setProduceKey('');
      setProduceValue('');
    } catch { setError('Failed to produce'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleExecuteSql = async () => {
    if (!sqlQuery.trim()) return;
    try {
      setLoading(true);
      setSqlResults(null);
      const res = await apiService.executeSql(cluster.id, sqlQuery);
      if (res.success) setSqlResults(res);
      else throw new Error(res.message);
    } catch (e) { setError('SQL Error: ' + e.message); }
    finally { setLoading(false); }
  };

  const handleStartStream = async () => {
    if (!selectedTopic) return;
    try {
      setLoading(true);
      setStreamMessages([]);
      await apiService.startMessageStream(cluster.id, selectedTopic);
      setStreamEnabled(true);
    } catch { setError('Failed to start stream'); setStreamEnabled(false); }
    finally { setLoading(false); }
  };

  const handleStopStream = async () => {
    try { await apiService.stopMessageStream(cluster.id, selectedTopic); } catch {}
    setStreamEnabled(false);
    if (streamCleanupRef.current) streamCleanupRef.current();
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm(`Delete group "${groupId}"?`)) return;
    try {
      setLoading(true);
      await apiService.deleteConsumerGroup(cluster.id, groupId);
      setSuccess('Group deleted');
      loadConsumerGroups();
      if (selectedTopic) loadTopicConsumerGroups(selectedTopic);
    } catch { setError('Failed to delete group'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleResetOffsets = async (groupId, offset) => {
    if (!selectedTopic) return;
    try {
      setLoading(true);
      await apiService.resetConsumerGroupOffsets(cluster.id, groupId, selectedTopic, offset);
      setSuccess('Offsets reset');
      loadTopicConsumerGroups(selectedTopic);
    } catch { setError('Failed to reset offsets'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  // Export
  const exportMessages = (format) => {
    const data = messages.length > 0 ? messages : streamMessages;
    if (!data.length) return;
    
    let content, filename, type;
    if (format === 'json') {
      content = JSON.stringify(data, null, 2);
      filename = `${selectedTopic}_${Date.now()}.json`;
      type = 'application/json';
    } else {
      const headers = ['partition', 'offset', 'timestamp', 'key', 'value'];
      content = [headers.join(','), ...data.map(m => headers.map(h => `"${(m[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
      filename = `${selectedTopic}_${Date.now()}.csv`;
      type = 'text/csv';
    }
    
    const blob = new Blob([content], { type });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  // SSH Terminal
  const openSSHTerminal = (node) => {
    const saved = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (!saved) {
      setPendingAction({ type: 'ssh', node });
      setShowCredentialsDialog(true);
      return;
    }
    setSSHNode(node);
    setShowSSHTerminal(true);
  };

  // Credentials & Actions
  const handleCredentialsSubmit = () => {
    localStorage.setItem(`cluster_creds_${cluster.id}`, JSON.stringify(credentials));
    setShowCredentialsDialog(false);
    if (pendingAction?.type === 'ssh') {
      setSSHNode(pendingAction.node);
      setShowSSHTerminal(true);
    } else if (pendingAction?.type === 'uninstall') setShowUninstallDialog(true);
    else if (pendingAction?.type === 'ssl') handleDownloadSSL();
    else if (pendingAction?.type === 'addNodes') {
      setShowAddNodesDialog(true);
      setTimeout(() => handleAddNodes(), 100);
    }
    setPendingAction(null);
  };

  const handleUninstall = async () => {
    if (uninstallConfirmText !== cluster.name) return setError('Type cluster name');
    const saved = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (!saved) { setPendingAction({ type: 'uninstall' }); setShowUninstallDialog(false); setShowCredentialsDialog(true); return; }
    try {
      setLoading(true);
      await apiService.uninstallCluster(cluster.id, JSON.parse(saved), true);
      localStorage.removeItem(`cluster_creds_${cluster.id}`);
      onBack();
    } catch { setError('Uninstall failed'); }
    finally { setLoading(false); }
  };

  const handleDownloadSSL = async () => {
    const saved = localStorage.getItem(`cluster_creds_${cluster.id}`);
    if (!saved) { setPendingAction({ type: 'ssl' }); setShowSSLDialog(false); setShowCredentialsDialog(true); return; }
    try {
      setLoading(true);
      const res = await apiService.downloadSSLCerts(cluster.id, JSON.parse(saved));
      if (res.success) {
        Object.values(res.files).forEach(f => {
          const content = f.encoding === 'base64' ? Uint8Array.from(atob(f.content), c => c.charCodeAt(0)) : f.content;
          const blob = new Blob([content]);
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = f.name;
          a.click();
        });
        setSuccess('Downloaded');
        setShowSSLDialog(false);
      }
    } catch { setError('Download failed'); }
    finally { setLoading(false); setTimeout(() => setSuccess(null), 3000); }
  };

  // Filtered data
  const filteredMsgs = messages.filter(m => !messageFilter || m.key?.toLowerCase().includes(messageFilter.toLowerCase()) || m.value?.toLowerCase().includes(messageFilter.toLowerCase()));
  const filteredStream = streamMessages.filter(m => !messageFilter || m.key?.toLowerCase().includes(messageFilter.toLowerCase()) || m.value?.toLowerCase().includes(messageFilter.toLowerCase())).slice(0, streamPageSize);

  const savedCreds = localStorage.getItem(`cluster_creds_${cluster.id}`);
  const creds = savedCreds ? JSON.parse(savedCreds) : credentials;

  return (
    <div className="space-y-4">
      {/* SSH Terminal */}
      {showSSHTerminal && sshNode && (
        <SSHTerminal
          host={sshNode.host}
          username={creds.username}
          password={creds.password}
          privateKey={creds.privateKey}
          onClose={() => setShowSSHTerminal(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ChevronLeft className="h-4 w-4" /></Button>
          <Database className="h-5 w-5 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold">{cluster.name}</h2>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setNewClusterName(cluster.name); setShowRenameDialog(true); }} title="Rename Cluster">
                <Pencil className="h-3 w-3" />
              </Button>
              {cluster.security === 'SSL' && <Badge variant="outline" className="text-green-600"><Shield className="h-3 w-3 mr-1" />SSL</Badge>}
            </div>
            <p className="text-xs text-muted-foreground">{cluster.mode} • v{cluster.version} • {cluster.nodes?.length} brokers</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddNodesDialog(true)}><Plus className="h-4 w-4 mr-1" />Add Nodes</Button>
          <Button variant="outline" size="sm" onClick={testNetworkConnectivity} disabled={loading}><RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} />Test Network</Button>
          {cluster.security === 'SSL' && <Button variant="outline" size="sm" onClick={() => setShowSSLDialog(true)}><Download className="h-4 w-4 mr-1" />SSL Certs</Button>}
          <Button variant="destructive" size="sm" onClick={() => setShowUninstallDialog(true)}><Trash2 className="h-4 w-4 mr-1" />Uninstall</Button>
        </div>
      </div>

      {/* Health Status Card */}
      <HealthStatusCard 
        health={clusterHealth} 
        loading={healthLoading} 
        onRefresh={() => { loadClusterHealth(); loadNodeStats(); }}
        nodes={cluster.nodes}
        onSSH={openSSHTerminal}
        nodeStats={nodeStats}
        nodeStatsLoading={nodeStatsLoading}
        onRefreshStats={loadNodeStats}
      />

      {/* Alerts */}
      {error && <Alert variant="destructive" className="py-2"><AlertTriangle className="h-4 w-4" /><AlertDescription className="ml-2 text-xs">{error}</AlertDescription><Button variant="ghost" size="sm" className="ml-auto" onClick={() => setError(null)}>×</Button></Alert>}
      {success && <Alert className="py-2 border-green-500/50 bg-green-500/10"><CheckCircle className="h-4 w-4 text-green-500" /><AlertDescription className="ml-2 text-xs text-green-600">{success}</AlertDescription></Alert>}

      {/* Main Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b px-4 py-3">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="topics"><MessageSquare className="h-4 w-4 mr-2" />Topics</TabsTrigger>
              <TabsTrigger value="messages" disabled={!selectedTopic}><Table className="h-4 w-4 mr-2" />Messages</TabsTrigger>
              <TabsTrigger value="stream" disabled={!selectedTopic}><Radio className="h-4 w-4 mr-2" />Live</TabsTrigger>
              <TabsTrigger value="produce" disabled={!selectedTopic}><Send className="h-4 w-4 mr-2" />Produce</TabsTrigger>
              <TabsTrigger value="sql"><Code className="h-4 w-4 mr-2" />SQL</TabsTrigger>
              <TabsTrigger value="consumers" onClick={loadConsumerGroups}><Users className="h-4 w-4 mr-2" />Consumers</TabsTrigger>
              <TabsTrigger value="ksqldb"><Database className="h-4 w-4 mr-2" />ksqlDB</TabsTrigger>
              <TabsTrigger value="security"><Shield className="h-4 w-4 mr-2" />Security</TabsTrigger>
            </TabsList>
          </div>

          <CardContent className="p-4">
            {/* Topics */}
            <TabsContent value="topics" className="mt-0">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm">Topics ({topics.length})</span>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={loadTopics} disabled={loading}><RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /></Button>
                      <Button size="sm" onClick={() => setShowCreateTopic(true)}><Plus className="h-3 w-3 mr-1" />New</Button>
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                    <Input 
                      placeholder="Search topics..." 
                      value={topicSearch} 
                      onChange={e => setTopicSearch(e.target.value)} 
                      className="h-8 pl-8 text-xs" 
                    />
                  </div>
                  <div className="space-y-1 max-h-[360px] overflow-y-auto">
                    {topics.filter(t => !topicSearch || t.toLowerCase().includes(topicSearch.toLowerCase())).map(t => (
                      <div key={t} className={cn("flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted/50 group", selectedTopic === t && "bg-primary/10 border-primary/50 border")} onClick={() => handleSelectTopic(t)}>
                        <span className="text-sm truncate">{t}</span>
                        <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100" onClick={(e) => { e.stopPropagation(); handleDeleteTopic(t); }}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    ))}
                    {topics.filter(t => !topicSearch || t.toLowerCase().includes(topicSearch.toLowerCase())).length === 0 && (
                      <p className="text-center text-muted-foreground text-sm py-4">
                        {loading ? 'Loading...' : topicSearch ? 'No topics match your search' : 'No topics'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  {topicDetails ? (
                    <Card className="p-4">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="font-bold">{selectedTopic}</h3>
                        <Button variant="outline" size="sm" onClick={() => setShowEditTopic(true)}><Edit className="h-3 w-3 mr-1" />Edit</Button>
                      </div>
                      <div className="grid grid-cols-4 gap-3 mb-4">
                        <div className="text-center p-3 rounded bg-muted/50"><p className="text-2xl font-bold text-primary">{topicDetails.partitionCount}</p><p className="text-xs text-muted-foreground">Partitions</p></div>
                        <div className="text-center p-3 rounded bg-muted/50"><p className="text-2xl font-bold text-primary">{topicDetails.replicationFactor}</p><p className="text-xs text-muted-foreground">Replication</p></div>
                        <div className="text-center p-3 rounded bg-muted/50"><p className="text-2xl font-bold text-green-500">{topicDetails.partitions?.filter(p => p.isr?.length === p.replicas?.length).length}</p><p className="text-xs text-muted-foreground">In-Sync</p></div>
                        <div className="text-center p-3 rounded bg-muted/50"><p className="text-2xl font-bold text-blue-500">{topicConsumerGroups.length}</p><p className="text-xs text-muted-foreground">Consumers</p></div>
                      </div>
                      
                      {topicConsumerGroups.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold mb-2">Consumer Groups</h4>
                          <div className="space-y-1">
                            {topicConsumerGroups.map(g => (
                              <div key={g.groupId} className="flex items-center justify-between p-2 rounded bg-muted/30 text-xs">
                                <span className="font-mono">{g.groupId}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant={g.state === 'Stable' ? 'success' : 'secondary'}>{g.state}</Badge>
                                  <span>Lag: <strong className={g.lag > 1000 ? 'text-red-500' : 'text-green-500'}>{g.lag}</strong></span>
                                  <Button variant="ghost" size="sm" className="h-6" onClick={() => handleResetOffsets(g.groupId, 'latest')}><RotateCcw className="h-3 w-3 mr-1" />Reset</Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => { setActiveTab('messages'); loadMessages(1); }}><Table className="h-3 w-3 mr-1" />Messages</Button>
                        <Button size="sm" variant="secondary" onClick={() => setActiveTab('stream')}><Radio className="h-3 w-3 mr-1" />Live</Button>
                        <Button size="sm" variant="outline" onClick={() => setActiveTab('produce')}><Send className="h-3 w-3 mr-1" />Produce</Button>
                      </div>
                    </Card>
                  ) : (
                    <Card className="p-8 text-center"><MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" /><p className="text-muted-foreground">Select a topic</p></Card>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Messages */}
            <TabsContent value="messages" className="mt-0">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{selectedTopic}</span>
                    <Badge variant="outline">{totalMessages} total</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Filter..." value={messageFilter} onChange={e => setMessageFilter(e.target.value)} className="h-8 w-40 text-xs" />
                    <Select value={pageSize.toString()} onValueChange={v => { setPageSize(parseInt(v)); }}>
                      <SelectTrigger className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[10, 25, 50, 100].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={() => loadMessages(1, pageSize)} disabled={messagesLoading}><RefreshCw className={cn("h-3 w-3 mr-1", messagesLoading && "animate-spin")} />Load</Button>
                    <Button size="sm" variant="outline" onClick={() => exportMessages('json')} disabled={!messages.length}><FileDown className="h-3 w-3 mr-1" />JSON</Button>
                    <Button size="sm" variant="outline" onClick={() => exportMessages('csv')} disabled={!messages.length}>CSV</Button>
                  </div>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                    <div className="col-span-1">Offset</div>
                    <div className="col-span-2">Timestamp</div>
                    <div className="col-span-2">Key</div>
                    <div className="col-span-5">Value</div>
                    <div className="col-span-2 text-right">Actions</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {filteredMsgs.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">{messagesLoading ? 'Loading messages...' : 'No messages. Click "Load" to fetch messages.'}</div>
                    ) : (
                      filteredMsgs.map((m, i) => (
                        <div key={`${m.partition}-${m.offset}-${i}`} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-start">
                          <div className="col-span-1 text-muted-foreground font-mono">P{m.partition}:{m.offset}</div>
                          <div className="col-span-2 text-muted-foreground">{m.timestamp ? new Date(m.timestamp).toLocaleString() : '-'}</div>
                          <div className="col-span-2 font-mono truncate">{m.key || <span className="text-muted-foreground italic">null</span>}</div>
                          <div className="col-span-5"><JsonViewer data={m.value} /></div>
                          <div className="col-span-2 flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setJsonViewerData(m); setShowJsonViewer(true); }} title="View"><Eye className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(m.value)} title="Copy"><Copy className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Page {currentPage} of {totalPages}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" disabled={currentPage <= 1 || messagesLoading} onClick={() => loadMessages(currentPage - 1, pageSize)}><ChevronLeft className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" disabled={currentPage >= totalPages || messagesLoading} onClick={() => loadMessages(currentPage + 1, pageSize)}><ChevronRight className="h-3 w-3" /></Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Live Stream */}
            <TabsContent value="stream" className="mt-0">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">Live: {selectedTopic}</span>
                    {streamEnabled && <Badge variant="success" className="animate-pulse"><Radio className="h-3 w-3 mr-1" />Live</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input placeholder="Filter..." value={messageFilter} onChange={e => setMessageFilter(e.target.value)} className="h-8 w-40 text-xs" />
                    <Select value={streamPageSize.toString()} onValueChange={v => setStreamPageSize(parseInt(v))}>
                      <SelectTrigger className="h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[25, 50, 100, 200].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {streamEnabled ? (
                      <Button size="sm" variant="destructive" onClick={handleStopStream}><Square className="h-3 w-3 mr-1" />Stop</Button>
                    ) : (
                      <Button size="sm" onClick={handleStartStream} disabled={loading}><Play className="h-3 w-3 mr-1" />Start</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => exportMessages('json')} disabled={!filteredStream.length}><FileDown className="h-3 w-3 mr-1" />Export</Button>
                  </div>
                </div>
                
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                    <div className="col-span-1">Offset</div>
                    <div className="col-span-2">Timestamp</div>
                    <div className="col-span-2">Key</div>
                    <div className="col-span-5">Value</div>
                    <div className="col-span-2 text-right">Actions</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {filteredStream.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">{streamEnabled ? 'Waiting for new messages...' : 'Click "Start" to begin live streaming'}</div>
                    ) : (
                      filteredStream.map((m, i) => (
                        <div key={`${m.partition}-${m.offset}-${i}`} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-start animate-in fade-in duration-300">
                          <div className="col-span-1 text-muted-foreground font-mono">P{m.partition}:{m.offset}</div>
                          <div className="col-span-2 text-muted-foreground">{m.timestamp ? new Date(m.timestamp).toLocaleString() : '-'}</div>
                          <div className="col-span-2 font-mono truncate">{m.key || <span className="text-muted-foreground italic">null</span>}</div>
                          <div className="col-span-5"><JsonViewer data={m.value} /></div>
                          <div className="col-span-2 flex justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(m.value)}><Copy className="h-3 w-3" /></Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Produce */}
            <TabsContent value="produce" className="mt-0">
              <div className="max-w-xl space-y-4">
                <h3 className="font-semibold">Produce to: {selectedTopic}</h3>
                <div className="space-y-3">
                  <div><Label className="text-xs">Key (optional)</Label><Input value={produceKey} onChange={e => setProduceKey(e.target.value)} placeholder="key" className="font-mono" /></div>
                  <div><Label className="text-xs">Value (JSON supported)</Label><Textarea value={produceValue} onChange={e => setProduceValue(e.target.value)} placeholder='{"event":"test","data":{"id":1}}' rows={8} className="font-mono" /></div>
                  <Button onClick={handleProduceMessage} disabled={loading || !produceValue}><Send className="h-4 w-4 mr-2" />{loading ? 'Sending...' : 'Send Message'}</Button>
                </div>
              </div>
            </TabsContent>

            {/* SQL */}
            <TabsContent value="sql" className="mt-0">
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between"><Label className="font-semibold">SQL Query</Label><span className="text-xs text-muted-foreground">SELECT * FROM topic [WHERE ...] [LIMIT n]</span></div>
                  <Textarea value={sqlQuery} onChange={e => setSqlQuery(e.target.value)} rows={3} className="font-mono" />
                  <div className="flex items-center gap-2">
                    <Button onClick={handleExecuteSql} disabled={loading}><Play className="h-4 w-4 mr-2" />{loading ? 'Running...' : 'Execute'}</Button>
                    <Select value={sqlPageSize.toString()} onValueChange={v => { setSqlPageSize(parseInt(v)); setSqlQuery(prev => prev.replace(/LIMIT \d+/i, `LIMIT ${v}`)); }}>
                      <SelectTrigger className="h-9 w-24 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[25, 50, 100, 200].map(n => <SelectItem key={n} value={n.toString()}>{n} rows</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {sqlResults && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm">Results from: {sqlResults.topic}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{sqlResults.count} rows</Badge>
                        <Button size="sm" variant="outline" onClick={() => { const blob = new Blob([JSON.stringify(sqlResults.results, null, 2)], { type: 'application/json' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `sql_${Date.now()}.json`; a.click(); }}><FileDown className="h-3 w-3 mr-1" />Export</Button>
                      </div>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                        <div className="col-span-1">Offset</div>
                        <div className="col-span-2">Timestamp</div>
                        <div className="col-span-2">Key</div>
                        <div className="col-span-6">Value</div>
                        <div className="col-span-1 text-right">Actions</div>
                      </div>
                      <div className="max-h-[350px] overflow-y-auto">
                        {sqlResults.results?.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground">No results found</div>
                        ) : (
                          sqlResults.results?.map((r, i) => (
                            <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-start">
                              <div className="col-span-1 text-muted-foreground font-mono">P{r.partition}:{r.offset}</div>
                              <div className="col-span-2 text-muted-foreground">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '-'}</div>
                              <div className="col-span-2 font-mono truncate">{r.key || <span className="text-muted-foreground italic">null</span>}</div>
                              <div className="col-span-6"><JsonViewer data={r.value} /></div>
                              <div className="col-span-1 flex justify-end">
                                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyToClipboard(r.value)} title="Copy"><Copy className="h-3 w-3" /></Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Consumer Groups */}
            <TabsContent value="consumers" className="mt-0">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm">Consumer Groups ({consumerGroups.length})</span>
                  <Button size="sm" variant="outline" onClick={loadConsumerGroups} disabled={loading}><RefreshCw className={cn("h-3 w-3 mr-1", loading && "animate-spin")} />Refresh</Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted/50 px-3 py-2 grid grid-cols-12 gap-2 text-xs font-semibold border-b">
                    <div className="col-span-5">Group ID</div>
                    <div className="col-span-2">State</div>
                    <div className="col-span-2">Protocol</div>
                    <div className="col-span-3 text-right">Actions</div>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    {consumerGroups.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">{loading ? 'Loading...' : 'No consumer groups'}</div>
                    ) : (
                      consumerGroups.map(g => (
                        <div key={g.groupId} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs border-b border-border/50 hover:bg-muted/30 items-center">
                          <div className="col-span-5 font-mono truncate">{g.groupId}</div>
                          <div className="col-span-2"><Badge variant={g.state === 'Stable' ? 'success' : g.state === 'Empty' ? 'secondary' : 'warning'}>{g.state || 'Unknown'}</Badge></div>
                          <div className="col-span-2 text-muted-foreground">{g.protocolType || '-'}</div>
                          <div className="col-span-3 flex justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-6 text-xs text-destructive" onClick={() => handleDeleteGroup(g.groupId)}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ksqlDB */}
            <TabsContent value="ksqldb" className="mt-0">
              <KsqlDBManager clusterId={cluster.id} cluster={cluster} credentials={creds} />
            </TabsContent>

            {/* Security - ACLs & Users */}
            <TabsContent value="security" className="mt-0">
              <ACLManager clusterId={cluster.id} credentials={creds} />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Dialogs */}
      <Dialog open={showCreateTopic} onOpenChange={setShowCreateTopic}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Create Topic</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div><Label>Name</Label><Input value={newTopic.name} onChange={e => setNewTopic({ ...newTopic, name: e.target.value })} placeholder="my-topic" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Partitions</Label><Input type="number" value={newTopic.partitions} onChange={e => setNewTopic({ ...newTopic, partitions: parseInt(e.target.value) || 1 })} min={1} /></div>
              <div><Label>Replication</Label><Input type="number" value={newTopic.replicationFactor} onChange={e => setNewTopic({ ...newTopic, replicationFactor: parseInt(e.target.value) || 1 })} min={1} max={cluster.nodes?.length} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTopic(false)}>Cancel</Button>
            <Button onClick={handleCreateTopic} disabled={!newTopic.name || loading}>{loading ? 'Creating...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditTopic} onOpenChange={setShowEditTopic}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Topic: {selectedTopic}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Add Partitions (current: {topicDetails?.partitionCount})</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" value={newPartitions} onChange={e => setNewPartitions(parseInt(e.target.value) || topicDetails?.partitionCount)} min={topicDetails?.partitionCount} />
                <Button onClick={handleAddPartitions} disabled={newPartitions <= topicDetails?.partitionCount || loading}>Add</Button>
              </div>
            </div>
            <Separator />
            <div>
              <Label>Retention (ms)</Label>
              <Input value={editConfig['retention.ms'] || ''} onChange={e => setEditConfig({ ...editConfig, 'retention.ms': e.target.value })} placeholder="604800000 (7 days)" />
            </div>
            <div>
              <Label>Cleanup Policy</Label>
              <Select value={editConfig['cleanup.policy'] || 'delete'} onValueChange={v => setEditConfig({ ...editConfig, 'cleanup.policy': v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="delete">delete</SelectItem>
                  <SelectItem value="compact">compact</SelectItem>
                  <SelectItem value="delete,compact">delete,compact</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditTopic(false)}>Cancel</Button>
            <Button onClick={handleUpdateConfig} disabled={loading}>{loading ? 'Saving...' : 'Save Config'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showJsonViewer} onOpenChange={setShowJsonViewer}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle>Message Details</DialogTitle></DialogHeader>
          <div className="space-y-4 overflow-y-auto max-h-[60vh]">
            {jsonViewerData && (
              <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>Partition:</strong> {jsonViewerData.partition}</div>
                  <div><strong>Offset:</strong> {jsonViewerData.offset}</div>
                  <div className="col-span-2"><strong>Timestamp:</strong> {jsonViewerData.timestamp ? new Date(jsonViewerData.timestamp).toLocaleString() : '-'}</div>
                  <div className="col-span-2"><strong>Key:</strong> <code className="bg-muted px-1 rounded">{jsonViewerData.key || 'null'}</code></div>
                </div>
                <Separator />
                <div>
                  <Label>Value</Label>
                  <pre className="mt-2 p-4 bg-gray-900 text-green-400 rounded-lg overflow-auto max-h-[300px] text-xs font-mono whitespace-pre-wrap">
                    {(() => { try { return JSON.stringify(JSON.parse(jsonViewerData.value), null, 2); } catch { return jsonViewerData.value; } })()}
                  </pre>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => copyToClipboard(jsonViewerData?.value)}><Copy className="h-4 w-4 mr-2" />Copy Value</Button>
            <Button onClick={() => setShowJsonViewer(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCredentialsDialog} onOpenChange={setShowCredentialsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>SSH Credentials</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <div><Label>Username</Label><Input value={credentials.username} onChange={e => setCredentials({ ...credentials, username: e.target.value })} placeholder="ec2-user" /></div>
            <Tabs value={credentials.authType} onValueChange={v => setCredentials({ ...credentials, authType: v })}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="password"><Lock className="h-3 w-3 mr-1" />Password</TabsTrigger>
                <TabsTrigger value="key"><KeyRound className="h-3 w-3 mr-1" />SSH Key</TabsTrigger>
              </TabsList>
              <TabsContent value="password" className="mt-3"><Input type="password" value={credentials.password} onChange={e => setCredentials({ ...credentials, password: e.target.value })} placeholder="Password" /></TabsContent>
              <TabsContent value="key" className="mt-3"><Textarea value={credentials.privateKey} onChange={e => setCredentials({ ...credentials, privateKey: e.target.value })} placeholder="-----BEGIN RSA PRIVATE KEY-----" rows={5} className="font-mono text-xs" /></TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCredentialsDialog(false)}>Cancel</Button>
            <Button onClick={handleCredentialsSubmit} disabled={!credentials.username}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUninstallDialog} onOpenChange={setShowUninstallDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-destructive flex items-center gap-2"><Trash2 className="h-4 w-4" />Uninstall Cluster</DialogTitle></DialogHeader>
          <div className="space-y-4 py-3">
            <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription className="ml-2">This will permanently remove Kafka from all nodes!</AlertDescription></Alert>
            <p className="text-sm">Type <strong>{cluster.name}</strong> to confirm:</p>
            <Input value={uninstallConfirmText} onChange={e => setUninstallConfirmText(e.target.value)} placeholder="Type cluster name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUninstallDialog(false); setUninstallConfirmText(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleUninstall} disabled={uninstallConfirmText !== cluster.name || loading}>{loading ? 'Uninstalling...' : 'Uninstall'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSSLDialog} onOpenChange={setShowSSLDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Shield className="h-4 w-4" />Download SSL Certificates</DialogTitle></DialogHeader>
          <div className="py-3">
            <p className="text-sm text-muted-foreground mb-2">Download SSL certificates for client connections:</p>
            <ul className="text-sm space-y-1 ml-4 list-disc">
              <li>kafka.truststore.jks</li>
              <li>ca-cert.pem</li>
              <li>client.properties</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSSLDialog(false)}>Cancel</Button>
            <Button onClick={handleDownloadSSL} disabled={loading}><Download className="h-4 w-4 mr-1" />{loading ? 'Downloading...' : 'Download'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4" />Rename Cluster</DialogTitle></DialogHeader>
          <div className="py-3">
            <Label>Cluster Name</Label>
            <Input value={newClusterName} onChange={e => setNewClusterName(e.target.value)} placeholder="Enter new name" className="mt-2" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>Cancel</Button>
            <Button onClick={handleRenameCluster} disabled={loading || !newClusterName.trim()}>{loading ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Nodes Dialog */}
      <Dialog open={showAddNodesDialog} onOpenChange={(open) => { if (!addNodesLoading) { setShowAddNodesDialog(open); if (!open) { setAddNodeType('broker'); } } }}>  
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              Add Nodes to Cluster
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-3">
            {/* Cluster Info */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-4 text-sm">
                <div><span className="text-muted-foreground">Cluster:</span> <strong>{cluster.name}</strong></div>
                <div><span className="text-muted-foreground">Mode:</span> <Badge variant="outline">{cluster.mode}</Badge></div>
                <div><span className="text-muted-foreground">Current Nodes:</span> <Badge>{cluster.nodes?.length || 0}</Badge></div>
              </div>
            </div>

            {/* Node Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Node Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {/* Broker Only Option */}
                <div 
                  className={cn(
                    "p-3 border rounded-lg cursor-pointer transition-all",
                    addNodeType === 'broker' 
                      ? "border-primary bg-primary/5 ring-1 ring-primary" 
                      : "border-border hover:border-primary/50"
                  )}
                  onClick={() => !addNodesLoading && setAddNodeType('broker')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={cn("w-4 h-4 rounded-full border-2 flex items-center justify-center", addNodeType === 'broker' ? "border-primary" : "border-muted-foreground")}>
                      {addNodeType === 'broker' && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <span className="font-medium text-sm">Broker Only</span>
                    <Badge variant="secondary" className="text-xs">Recommended</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    {cluster.mode === 'KRaft' 
                      ? 'Add Kafka broker nodes only. They will connect to existing controllers for metadata.'
                      : 'Add Kafka broker nodes only. They will connect to existing ZooKeeper ensemble.'
                    }
                  </p>
                </div>

                {/* Full Node Option */}
                <div 
                  className={cn(
                    "p-3 border rounded-lg cursor-pointer transition-all",
                    addNodeType === 'full' 
                      ? "border-primary bg-primary/5 ring-1 ring-primary" 
                      : "border-border hover:border-primary/50",
                    (cluster.nodes?.length || 0) >= 3 && "opacity-60"
                  )}
                  onClick={() => !addNodesLoading && setAddNodeType('full')}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={cn("w-4 h-4 rounded-full border-2 flex items-center justify-center", addNodeType === 'full' ? "border-primary" : "border-muted-foreground")}>
                      {addNodeType === 'full' && <div className="w-2 h-2 rounded-full bg-primary" />}
                    </div>
                    <span className="font-medium text-sm">
                      {cluster.mode === 'KRaft' ? 'Broker + Controller' : 'Broker + ZooKeeper'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6">
                    {cluster.mode === 'KRaft' 
                      ? 'Add full nodes with both broker and controller roles. Use for cluster redundancy.'
                      : 'Add full nodes with both Kafka broker and ZooKeeper. Updates ZK quorum config.'
                    }
                  </p>
                  {(cluster.nodes?.length || 0) >= 3 && (
                    <p className="text-xs text-yellow-600 ml-6 mt-1">
                      ⚠️ You already have {cluster.nodes?.length} nodes. {cluster.mode === 'KRaft' ? 'Controllers' : 'ZooKeeper'} typically only needs 3 nodes.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Info Alert based on selection */}
            <Alert className={addNodeType === 'broker' ? 'border-green-500/50 bg-green-500/5' : 'border-yellow-500/50 bg-yellow-500/5'}>
              <Server className="h-4 w-4" />
              <AlertDescription className="ml-2 text-xs">
                {addNodeType === 'broker' ? (
                  cluster.mode === 'KRaft' 
                    ? 'New broker-only nodes will join the cluster and start accepting partitions. They do NOT participate in controller elections.'
                    : 'New broker-only nodes will connect to the existing ZooKeeper ensemble. No ZooKeeper changes needed.'
                ) : (
                  cluster.mode === 'KRaft' 
                    ? 'WARNING: Adding new controllers requires careful planning. The controller quorum will be updated.'
                    : 'WARNING: Adding ZooKeeper nodes will update the ensemble configuration on ALL existing nodes and trigger restarts.'
                )}
              </AlertDescription>
            </Alert>

            {/* New Nodes List */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">New {addNodeType === 'broker' ? 'Broker' : 'Full'} Nodes</Label>
                <Button variant="outline" size="sm" onClick={addNewNodeRow} disabled={addNodesLoading}>
                  <Plus className="h-3 w-3 mr-1" />Add Row
                </Button>
              </div>
              
              <div className="space-y-2">
                {newNodes.map((node, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 border rounded-lg bg-background">
                    <div className="flex-1">
                      <Input
                        placeholder="Public IP / Hostname *"
                        value={node.host}
                        onChange={(e) => updateNodeRow(index, 'host', e.target.value)}
                        disabled={addNodesLoading}
                        className="h-9"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Private IP (optional)"
                        value={node.privateIp}
                        onChange={(e) => updateNodeRow(index, 'privateIp', e.target.value)}
                        disabled={addNodesLoading}
                        className="h-9"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-destructive hover:text-destructive"
                      onClick={() => removeNodeRow(index)}
                      disabled={newNodes.length === 1 || addNodesLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                * Public IP is required for SSH connection. Private IP is used for inter-broker communication (auto-detected if not provided).
              </p>
            </div>

            {/* Progress Logs */}
            {addNodesLoading && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="text-sm font-medium">Adding {addNodeType === 'broker' ? 'broker' : 'full'} nodes to cluster...</span>
                </div>
                <div className="h-40 bg-gray-900 rounded-lg p-3 overflow-y-auto font-mono text-xs">
                  {addNodesLogs.length === 0 ? (
                    <div className="text-gray-500">Waiting for logs...</div>
                  ) : (
                    addNodesLogs.map((log, i) => (
                      <div key={i} className={cn(
                        "py-0.5",
                        log.includes('[ERROR]') ? 'text-red-400' :
                        log.includes('[WARN]') ? 'text-yellow-400' :
                        log.includes('✓') || log.includes('✅') ? 'text-green-400' :
                        'text-gray-300'
                      )}>
                        {log}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Completed Logs */}
            {!addNodesLoading && addNodesLogs.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Installation Log</Label>
                <div className="h-40 bg-gray-900 rounded-lg p-3 overflow-y-auto font-mono text-xs">
                  {addNodesLogs.map((log, i) => (
                    <div key={i} className={cn(
                      "py-0.5",
                      log.includes('[ERROR]') ? 'text-red-400' :
                      log.includes('[WARN]') ? 'text-yellow-400' :
                      log.includes('✓') || log.includes('✅') ? 'text-green-400' :
                      'text-gray-300'
                    )}>
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAddNodesDialog(false);
                setNewNodes([{ host: '', privateIp: '' }]);
                setAddNodesLogs([]);
                setAddNodeType('broker');
              }}
              disabled={addNodesLoading}
            >
              {addNodesLogs.length > 0 && !addNodesLoading ? 'Close' : 'Cancel'}
            </Button>
            {(!addNodesLogs.length || addNodesLoading) && (
              <Button 
                onClick={handleAddNodes} 
                disabled={addNodesLoading || !newNodes.some(n => n.host.trim())}
              >
                {addNodesLoading ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Adding Nodes...</>
                ) : (
                  <><Plus className="h-4 w-4 mr-2" />Add {newNodes.filter(n => n.host.trim()).length} {addNodeType === 'broker' ? 'Broker' : 'Full'} Node(s)</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ClusterDashboard;
